import React, { useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

interface TopLeftMenuProps {
  onClose: () => void;
  onSettings: () => void;
  onProfile: () => void;
  onLogout: () => void;
}

const TopLeftMenu: React.FC<TopLeftMenuProps> = ({ onClose, onSettings, onProfile, onLogout }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const host = document.createElement('div');
    hostRef.current = host;
    document.body.appendChild(host);

    const onDocClick = (e: MouseEvent) => {
      if (!hostRef.current) return;
      const target = e.target as Node | null;
      if (!target) return;
      // If click is outside our host container, close
      if (hostRef.current && !hostRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', onDocClick);

    return () => {
      document.removeEventListener('mousedown', onDocClick);
      if (hostRef.current && hostRef.current.parentNode) {
        hostRef.current.parentNode.removeChild(hostRef.current);
      }
      hostRef.current = null;
    };
  }, [onClose]);

  if (typeof document === 'undefined') {
    // Fallback render for non-DOM environments
    return (
      <View style={styles.container}>
        <TouchableOpacity style={styles.item} onPress={() => { onSettings(); onClose(); }}>
          <Text style={styles.itemText}>Settings</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.item} onPress={() => { onProfile(); onClose(); }}>
          <Text style={styles.itemText}>Profile</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.item} onPress={() => { onLogout(); onClose(); }}>
          <Text style={[styles.itemText, { color: '#E74C3C' }]}>Logout</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Dynamic portal render
  const host = hostRef.current;
  const menu = (
    <div style={webStyles.wrapper as any} ref={containerRef}>
      <div style={webStyles.container as any}>
        <button style={webStyles.item as any} onClick={() => { onSettings(); onClose(); }}>Settings</button>
        <button style={webStyles.item as any} onClick={() => { onProfile(); onClose(); }}>Profile</button>
        <button style={{ ...webStyles.item as any, color: '#E74C3C' }} onClick={() => { onLogout(); onClose(); }}>Logout</button>
      </div>
    </div>
  );

  try {
    // Import react-dom synchronously via require to avoid bundler issues in some setups
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const ReactDOM = require('react-dom');
    if (host) {
      return ReactDOM.createPortal(menu, host);
    }
  } catch (err) {
    // If portal fails, render inline fallback
    return (
      <View style={styles.container}>
        <TouchableOpacity style={styles.item} onPress={() => { onSettings(); onClose(); }}>
          <Text style={styles.itemText}>Settings</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.item} onPress={() => { onProfile(); onClose(); }}>
          <Text style={styles.itemText}>Profile</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.item} onPress={() => { onLogout(); onClose(); }}>
          <Text style={[styles.itemText, { color: '#E74C3C' }]}>Logout</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return null;
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 56,
    left: 12,
    width: 180,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 8,
    zIndex: 99999,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)'
  },
  item: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  itemText: {
    fontSize: 15,
    color: '#111827',
  }
});

const webStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 2147483648,
  },
  container: {
    position: 'absolute',
    top: 56,
    left: 12,
    width: 200,
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    border: '1px solid rgba(0,0,0,0.06)',
    pointerEvents: 'auto',
    padding: '6px 0',
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    background: 'transparent',
    border: 'none',
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: 15,
    cursor: 'pointer',
  }
};

export default TopLeftMenu;
